// core/offline/offline.interceptor.ts
import { HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map, switchMap, tap, take } from 'rxjs/operators';
import { NetworkStatusService } from 'app/network-status.service';
import { OFFLINE_META, OUTBOX_ID, REPLAYING, scopeKeyFromArgs, getServerId, isClientId, newClientId } from '../../db/offline-context';
import { entityRepo, outboxRepo } from '../../db/offline-database';

type WriteMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE';
type UnknownRecord = Record<string, unknown>;

function isObject(v: unknown): v is UnknownRecord { return !!v && typeof v === 'object'; }
function toArrayOrEmpty(v: unknown): unknown[] { return Array.isArray(v) ? v : []; }
function pickListFromBody(body: unknown, selectList?: (b: unknown) => unknown[]): unknown[] {
    if (selectList) { try { return toArrayOrEmpty(selectList(body)); } catch { return []; } }
    if (Array.isArray(body)) return body;
    if (isObject(body)) {
        const d = (body as UnknownRecord)['data']; if (Array.isArray(d)) return d;
        const r = (body as UnknownRecord)['results']; if (Array.isArray(r)) return r;
    }
    return [];
}

export const offlineInterceptor: HttpInterceptorFn = (req, next): Observable<HttpEvent<any>> => {
    const net = inject(NetworkStatusService);
    const meta = req.context.get(OFFLINE_META);
    if (!meta) return next(req);

    const isReplay = req.context.get(REPLAYING) === true;

    // ---------- READ ----------
    if (meta.kind === 'read') {
        const scopeKey = scopeKeyFromArgs(meta.scopeArgs);
        return net.online$.pipe(take(1), switchMap(isOnline => {
            if (!isOnline) {
                return fromCache(req.url, meta.collection, scopeKey, meta);
            }
            return next(req).pipe(
                tap(ev => {
                    if (!(ev instanceof HttpResponse) || !ev.ok) return;

                    if (meta.isSingle) {
                        const entity: any = ev.body;
                        const sid = getServerId(entity, meta.serverIdOf);
                        if (sid) entityRepo.upsertByServerId(meta.collection, scopeKey, sid, entity);
                        return;
                    }

                    const raw: unknown = ev.body;
                    const list = pickListFromBody(raw, meta.selectList);
                    if (meta.fullReplace) {
                        entityRepo.replaceAllByServerId(
                            meta.collection, scopeKey, list as any[],
                            meta.serverIdOf as any, meta.subsetKey ?? null
                        );
                    } else {
                        for (const e of list) {
                            const sid = getServerId(e, meta.serverIdOf);
                            if (sid) entityRepo.upsertByServerId(meta.collection, scopeKey, sid, e, { pending: false });
                        }
                    }
                })
            );
        }));
    }

    // ---------- MUTATE ----------
    if (meta.kind === 'mutate') {
        const scopeKey = scopeKeyFromArgs(meta.scopeArgs);
        const isWrite = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method.toUpperCase());
        if (!isWrite) return next(req);

        // Replays gelten als "online"
        const online$ = isReplay ? of(true) : net.online$.pipe(take(1));

        return online$.pipe(
            switchMap(isOnline => {
                if (!isOnline) {
                    // OFFLINE: optimistic + Outbox (ATOMAR: erst schreiben, dann 202 senden)
                    const orig: any = req.body ?? null;
                    const fake: any = meta.optimisticBody?.(orig) ?? orig;
                    const cid = (fake?.id && isClientId(fake.id)) ? fake.id : newClientId();
                    const corrId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
                    const idFromBody = (fake && typeof fake === 'object') ? (fake as any).id : undefined;
                    const idFromUrl = meta.extractIdFromUrl?.(req.url) ?? undefined;
                    let anyId: string | undefined = idFromBody ?? idFromUrl;
                    // alle Dexie-Operationen sammeln und AWAITEN
                    const ops: Promise<any>[] = [];

                    if (meta.op === 'UPSERT') {
                        ops.push(
                            entityRepo.upsertLocal(
                                meta.collection,
                                scopeKey,
                                { ...fake, id: cid, __pending: true },
                                cid,
                                { pending: true }
                            )
                        );
                    } else if (meta.op === 'PATCH') {
                        // PATCH braucht eine ID; falls keine da, ignorieren wir das lokale Merge (Outbox geht trotzdem raus)
                        if (anyId) {
                            const patchObj = { ...(fake ?? {}), id: anyId };
                            ops.push(
                                entityRepo.mergeByAnyId(
                                    meta.collection, scopeKey, anyId,
                                    (prev: any) => ({ ...(prev ?? { id: anyId }), ...patchObj, __pending: true }),
                                    { pending: true }
                                )
                            );
                        }
                    } else if (meta.op === 'DELETE') {
                        const idFromBody = orig && typeof orig === 'object' ? (orig as any).id : undefined;
                        const delId = meta.extractIdFromUrl?.(req.url) ?? idFromBody;
                        if (delId) {
                            ops.push(entityRepo.removeByServerId(meta.collection, scopeKey, delId).catch(() => { }));
                            ops.push(entityRepo.removeByClientId(meta.collection, scopeKey, delId).catch(() => { }));
                        }
                    } else if (meta.op === 'CUSTOM') {
                        if (meta.applyLocal) {
                            ops.push(Promise.resolve(
                                meta.applyLocal({
                                    repo: entityRepo,
                                    collection: meta.collection,
                                    scopeKey,
                                    requestBody: fake,
                                    anyId
                                })
                            ));
                        }
                    }

                    // Outbox NACH dem Local-Write erfassen (und ebenfalls awaiten)
                    const outboxBody = { ...(orig ?? {}), ...(anyId ? { id: anyId } : {}) };
                    ops.push(
                        outboxRepo.add({
                            id: corrId,
                            method: req.method.toUpperCase() as WriteMethod,
                            url: req.urlWithParams,
                            body: outboxBody,
                            headers: {},
                            metaLight: { collection: meta.collection, scopeKey, op: meta.op, extractIdFromUrl: !!meta.extractIdFromUrl },
                            clientId: (anyId && isClientId(anyId)) ? anyId : undefined
                        })
                    );

                    // Fake-Response: UPSERT/PATCH/CUSTOM liefern ein Body mit __pending; DELETE liefert {success:true}
                    const fakeBody =
                        meta.op === 'DELETE'
                            ? { success: true }
                            : { ...(fake ?? {}), ...(anyId ? { id: anyId } : {}), __pending: true };

                    return from(Promise.all(ops)).pipe(
                        map(() => new HttpResponse({ status: 202, url: req.url, body: fakeBody }))
                    );
                }

                // ONLINE (+ REPLAY)
                return next(req).pipe(
                    tap(ev => {
                        if (!(ev instanceof HttpResponse) || !ev.ok) return;

                        // Outbox-ACK im Replay
                        if (isReplay) {
                            const corrId = req.context.get(OUTBOX_ID);
                            if (corrId) outboxRepo.remove(corrId).catch(() => { });
                        }

                        const reqBody: any = req.body ?? {};
                        const idFromBody = typeof reqBody === 'object' ? reqBody?.id : undefined;
                        const idFromUrl = meta.extractIdFromUrl?.(req.url) ?? undefined;
                        const anyId = idFromBody ?? idFromUrl;

                        if (meta.op === 'UPSERT') {
                            const entity: any = ev.body as any;
                            const sid = getServerId(entity);
                            const cid = isClientId(reqBody?.id) ? reqBody.id : undefined;

                            if (cid && sid) {
                                entityRepo.replaceClientWithServer(meta.collection, scopeKey, cid, sid, entity);
                            } else if (sid) {
                                entityRepo.upsertByServerId(meta.collection, scopeKey, sid, entity, { pending: false });
                            }
                        }

                        if (meta.op === 'PATCH' || meta.op === 'CUSTOM') {
                            const entity: any = ev.body as any;
                            const sid = entity ? getServerId(entity) : undefined;

                            if (sid && entity) {
                                // Server gibt komplettes Objekt zurück → normal einspielen
                                entityRepo.upsertByServerId(meta.collection, scopeKey, sid, entity, { pending: false });
                            } else if (anyId) {
                                // z.B. 204/{} → pending-Flag entfernen
                                entityRepo.mergeByAnyId(
                                    meta.collection, scopeKey, anyId,
                                    (prev: any) => {
                                        if (!prev) return prev;
                                        const { __pending, ...rest } = prev;
                                        return { ...rest };
                                    },
                                    { pending: false }
                                );
                            }
                        }

                        if (meta.op === 'DELETE') {
                            const delId = anyId ?? (ev.body as any)?.id;
                            if (delId) {
                                entityRepo.removeByServerId(meta.collection, scopeKey, delId).catch(() => { });
                                entityRepo.removeByClientId(meta.collection, scopeKey, delId).catch(() => { });
                            }
                        }
                    }),
                    // __replaces nur für UPSERT
                    map(ev => {
                        if (!(ev instanceof HttpResponse) || !ev.ok || meta.op !== 'UPSERT') return ev;
                        const reqBody: any = req.body ?? {};
                        const cid = isClientId(reqBody?.id) ? reqBody.id : undefined;
                        if (!cid) return ev;
                        return ev.clone({ body: { ...(ev.body as any), __replaces: cid } });
                    })
                );
            })
        );
    }

    return next(req);
};

// --- Offline-Reply aus Cache
import { Observable as RxObs } from 'rxjs';
function fromCache(url: string, collection: string, scopeKey: string, meta: any): RxObs<HttpEvent<any>> {
    return new RxObs<HttpEvent<any>>(sub => {
        entityRepo.getAll<any>(collection, scopeKey)
            .then(list => {
                const body = meta.isSingle ? (list[0] ?? null) : (meta.wrapList ? meta.wrapList(list, undefined as unknown) : list);
                sub.next(new HttpResponse({ status: 200, url, body }));
                sub.complete();
            })
            .catch(err => sub.error(err));
    });
}
